import { Button, Tag } from 'antd';
import { CheckOutlined, CloseOutlined, RightOutlined } from '@ant-design/icons';
import type { PendingChange } from '~/services/ai-chat';

interface ScratchpadProps {
  pendingChanges: PendingChange[];
  confirmedChanges: PendingChange[];
  onConfirm: (changes: PendingChange[]) => void;
  onDismiss: (changeId: string) => void;
  isConfirming: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
};

const ENTITY_LABELS: Record<string, string> = {
  booking: 'Booking',
  service_ticket: 'Service Ticket',
  inventory: 'Inventory',
};

const FIELD_LABELS: Record<string, string> = {
  serviceDatetime: 'Service Date',
  startDatetime: 'Start Time',
  serviceType: 'Service Type',
  scope: 'Scope',
  staffNotes: 'Staff Notes',
  urgency: 'Urgency',
  status: 'Status',
  bookingDate: 'Booking Date',
  sidemark: 'Sidemark',
  clientId: 'Client',
  addInventory: 'Add Inventory',
  removeInventory: 'Remove Inventory',
};

function ContextInfo({ context, entityType }: { context: Record<string, any>; entityType: string }) {
  if (entityType === 'service_ticket') {
    return (
      <div className="bg-gray-50 border border-gray-200 p-2 mb-2 text-[11px] space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">Booking</span>
          <span className="font-medium">{context.bookingRef || '—'}</span>
        </div>
        {context.client && (
          <div className="flex justify-between">
            <span className="text-gray-500">Client</span>
            <span className="font-medium">{context.client}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Service Type</span>
          <span className="font-medium">{context.serviceType?.replace(/_/g, ' ') || '—'}</span>
        </div>
        {context.duration != null && (
          <div className="flex justify-between">
            <span className="text-gray-500">Duration</span>
            <span className="font-medium">{context.duration}h</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Staff</span>
          <span className="font-medium">
            {context.staffCount || 0}
            {context.staff?.length > 0 && (
              <span className="text-gray-400 ml-1">({context.staff.join(', ')})</span>
            )}
          </span>
        </div>
        {context.vessel && (
          <div className="flex justify-between">
            <span className="text-gray-500">Vessel</span>
            <span className="font-medium">{context.vessel}</span>
          </div>
        )}
        {context.inventoryCount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Inventory</span>
            <span className="font-medium">{context.inventoryCount} items</span>
          </div>
        )}
      </div>
    );
  }

  if (entityType === 'booking') {
    return (
      <div className="bg-gray-50 border border-gray-200 p-2 mb-2 text-[11px] space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">Reference</span>
          <span className="font-medium">{context.referenceNumber || '—'}</span>
        </div>
        {context.client && (
          <div className="flex justify-between">
            <span className="text-gray-500">Client</span>
            <span className="font-medium">{context.client}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Status</span>
          <span className="font-medium">{context.status || '—'}</span>
        </div>
        {context.serviceTicketCount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Service Tickets</span>
            <span className="font-medium">{context.serviceTicketCount}</span>
          </div>
        )}
      </div>
    );
  }

  return null;
}

function formatValue(value: any): string {
  if (value == null) return '—';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(value);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (Array.isArray(value)) return `${value.length} items`;
  return String(value).replace(/_/g, ' ');
}

function ChangeField({ label, before, after }: { label: string; before?: any; after?: any }) {
  const displayLabel = FIELD_LABELS[label] || label;
  return (
    <div className="flex items-start gap-2 text-xs py-1">
      <span className="text-gray-500 min-w-[100px] font-medium">{displayLabel}:</span>
      <div className="flex items-center gap-1 flex-wrap">
        {before != null && (
          <>
            <span className="line-through text-red-400">{formatValue(before)}</span>
            <RightOutlined className="text-[8px] text-gray-400" />
          </>
        )}
        <span className="font-medium text-black">{formatValue(after)}</span>
      </div>
    </div>
  );
}

function ChangeCard({
  change,
  confirmed,
  onDismiss,
}: {
  change: PendingChange;
  confirmed: boolean;
  onDismiss?: (id: string) => void;
}) {
  const fields = Object.keys(change.after).filter(
    (k) => !['addInventory', 'removeInventory'].includes(k),
  );
  const hasInventoryAdd = change.after.addInventory?.length > 0;
  const hasInventoryRemove = change.after.removeInventory?.length > 0;

  return (
    <div
      className={`border p-3 mb-2 ${confirmed ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-white'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Tag color={ACTION_COLORS[change.action]} className="text-[10px] m-0">
            {change.action}
          </Tag>
          <span className="text-[10px] text-gray-400 uppercase">
            {ENTITY_LABELS[change.entityType] || change.entityType}
          </span>
        </div>
        {confirmed ? (
          <CheckOutlined className="text-green-500 text-xs" />
        ) : (
          onDismiss && (
            <button
              onClick={() => onDismiss(change.id)}
              className="text-gray-400 hover:text-red-500 text-xs"
            >
              <CloseOutlined />
            </button>
          )
        )}
      </div>

      <div className="text-xs font-medium mb-2">{change.label}</div>

      {/* Context info */}
      {change.context && (
        <ContextInfo context={change.context} entityType={change.entityType} />
      )}

      {/* Field changes */}
      <div className="border-t border-gray-100 pt-1">
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-1">
          Changes
        </div>
        {fields.map((key) => (
          <ChangeField
            key={key}
            label={key}
            before={change.before?.[key]}
            after={change.after[key]}
          />
        ))}
        {hasInventoryAdd && (
          <ChangeField label="addInventory" after={change.after.addInventory} />
        )}
        {hasInventoryRemove && (
          <ChangeField label="removeInventory" after={change.after.removeInventory} />
        )}
      </div>
    </div>
  );
}

export const Scratchpad = ({
  pendingChanges,
  confirmedChanges,
  onConfirm,
  onDismiss,
  isConfirming,
  isOpen,
  onToggle,
}: ScratchpadProps) => {
  const hasPending = pendingChanges.length > 0;
  const hasConfirmed = confirmedChanges.length > 0;
  const hasContent = hasPending || hasConfirmed;

  return (
    <>
      {/* Toggle tab on the right edge */}
      {!isOpen && hasContent && (
        <button
          onClick={onToggle}
          className="fixed right-0 top-1/2 -translate-y-1/2 bg-black text-white px-2 py-4 text-xs font-bold z-50 hover:bg-gray-800 transition-colors"
          style={{ writingMode: 'vertical-rl' }}
        >
          SCRATCHPAD {hasPending && `(${pendingChanges.length})`}
        </button>
      )}

      {/* Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l-2 border-black z-40 transition-transform duration-200 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: 380 }}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider">Scratchpad</h2>
          <button onClick={onToggle} className="text-gray-500 hover:text-black">
            <CloseOutlined />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {!hasContent && (
            <p className="text-xs text-gray-400 text-center mt-8">
              Pending changes from Oz will appear here for review before being applied.
            </p>
          )}

          {/* Pending changes */}
          {hasPending && (
            <div className="mb-4">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                Pending ({pendingChanges.length})
              </div>
              {pendingChanges.map((c) => (
                <ChangeCard key={c.id} change={c} confirmed={false} onDismiss={onDismiss} />
              ))}
            </div>
          )}

          {/* Confirmed changes (history) */}
          {hasConfirmed && (
            <div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                Applied ({confirmedChanges.length})
              </div>
              {confirmedChanges.map((c) => (
                <ChangeCard key={c.id} change={c} confirmed />
              ))}
            </div>
          )}
        </div>

        {/* Confirm button */}
        {hasPending && (
          <div className="px-4 py-3 border-t-2 border-black">
            <Button
              type="primary"
              block
              onClick={() => onConfirm(pendingChanges)}
              loading={isConfirming}
              className="!bg-black !border-black !rounded-none h-10 font-bold"
              icon={<CheckOutlined />}
            >
              Confirm & Apply ({pendingChanges.length} change{pendingChanges.length > 1 ? 's' : ''})
            </Button>
            <p className="text-[10px] text-gray-400 mt-2 text-center">
              Review all changes above before confirming.
            </p>
          </div>
        )}
      </div>
    </>
  );
};
